import { useState } from "react";
import { Link } from "react-router";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { AcademicPageHeader } from "@/components/records/AcademicWorkspace";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Bento, PageRefusal } from "@/components/ui/box";
import { useDriveBox } from "@/components/courses/course-page-boxes";
import { driveWords as w } from "@/components/drive/course-drive-copy";
import { useWorkspaceEyebrow } from "@/hooks/use-workspace-eyebrow";
import { formatTimestamp } from "@/components/settings/settings-copy";
import {
  courseDriveApi,
  courseDriveQueryKey,
  type CourseDriveCourse,
  type CourseDriveRegister,
} from "@/lib/api/courseDrive";
import { readyCourseFolderId } from "@/lib/api/driveContainers";
import { courseResourcesHref } from "@/navigation/course-workspace";
import { ApiError } from "@/lib/api/client";

function FolderSetup({
  course,
  query,
}: {
  course: CourseDriveCourse;
  query: UseQueryResult<CourseDriveRegister>;
}) {
  const box = useDriveBox({
    courseId: course.course_id,
    teaching: true,
    canEdit: course.can_create && !query.isError,
    termOpen: course.term?.status !== "closed",
    timezone: null,
    registerRead: {
      data: { containers: course.containers },
      dataUpdatedAt: query.dataUpdatedAt,
      error: query.error,
      isError: query.isError,
      isPending: query.isPending,
      refetch: query.refetch,
    },
  });
  return (
    <Bento
      id={`folder-setup-${course.course_id}`}
      journey="course-drive-setup"
      boxes={[box]}
    />
  );
}
function CourseFolder({
  course,
  query,
  visible,
}: {
  course: CourseDriveCourse;
  query: UseQueryResult<CourseDriveRegister>;
  visible: boolean;
}) {
  const folderId = readyCourseFolderId(course.containers);
  const current =
    course.containers.find((folder) => folder.state !== "failed") ??
    course.containers[0];
  const state =
    query.data?.availability !== "enabled"
      ? w.unavailableFolder
      : folderId
        ? w.linked
        : current?.state === "unknown"
          ? w.unknown
          : current?.state === "pending"
            ? w.pending
            : current?.state === "failed"
              ? w.failed
              : w.absent;
  const materialsHref = courseResourcesHref(course.href);
  return (
    <li
      className={
        visible
          ? "grid gap-4 border-b border-border py-5 last:border-b-0"
          : "hidden"
      }
      data-course-folder={course.course_id}
    >
      <div className="flex min-w-0 flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div className="min-w-0 space-y-1">
          <p className="text-meta text-muted-foreground">
            <span className="font-mono">{course.code}</span>
            {course.term?.name ? ` · ${course.term.name}` : ""}
            {course.crn ? ` · ${course.crn}` : ""}
          </p>
          <h3 className="break-words text-body font-semibold">
            {course.title}
          </h3>
          <p className="text-ui">{state}</p>
          {folderId && current?.folder_name_at_creation && (
            <p className="break-words text-meta text-muted-foreground">
              {w.folderName(current.folder_name_at_creation)}
            </p>
          )}
          {current?.state === "unknown" && (
            <p className="text-ui text-muted-foreground">{w.unknownHelp}</p>
          )}
          {!folderId && course.term?.status === "closed" && (
            <p className="text-ui text-muted-foreground">{w.closed}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
          <Link
            className="inline-flex min-h-[var(--control-height)] items-center text-ui font-semibold text-primary"
            to={materialsHref}
          >
            {w.materials}
          </Link>
        </div>
      </div>
      {query.data?.availability === "enabled" && (course.can_create || current) && (
        <FolderSetup course={course} query={query} />
      )}
    </li>
  );
}
export function CourseDrivePage() {
  const eyebrow = useWorkspaceEyebrow();
  const query = useQuery({
    queryKey: courseDriveQueryKey,
    queryFn: courseDriveApi.get,
    retry: false,
  });
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("current");
  const data = query.data;
  if (
    query.error instanceof ApiError &&
    [401, 403, 404].includes(query.error.status)
  )
    return (
      <PageRefusal
        id="course-drive"
        journey="course-drive"
        refusal={{
          reason: "forbidden",
          sentence: w.forbidden,
          back: { label: w.home, to: data?.shell_base ?? "/" },
        }}
      />
    );
  const filtered =
    data?.courses.filter((course) => {
      const past = course.term?.status === "closed";
      return (
        (term === "all" || (term === "past" ? past : !past)) &&
        [
          course.code,
          course.title,
          course.title_ar,
          course.crn,
          course.term?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase()
          .includes(search.trim().toLocaleLowerCase())
      );
    }) ?? [];
  return (
    <div className="app-page" data-drive-workspace>
      <AcademicPageHeader
        eyebrow={eyebrow}
        title={w.title}
        description={w.intro}
      />
      <Card>
        <CardContent className="space-y-2">
          <p className="text-ui text-muted-foreground">{w.guideLead}</p>
          <details>
            <summary className="cursor-pointer py-2 text-ui font-semibold">
              {w.guide}
            </summary>
            <div className="space-y-4 pt-3">
              <ol className="grid gap-4 sm:grid-cols-3">
                {w.steps.map(([title, detail], index) => (
                  <li key={title} className="space-y-1">
                    <p className="text-ui font-semibold">
                      {index + 1}. {title}
                    </p>
                    <p className="text-ui text-muted-foreground">{detail}</p>
                  </li>
                ))}
              </ol>
              <p className="text-ui text-muted-foreground">{w.access}</p>
              <p className="text-ui text-muted-foreground">{w.privacy}</p>
            </div>
          </details>
        </CardContent>
      </Card>
      {!data && query.isPending ? (
        <p role="status">{w.loading}</p>
      ) : !data ? (
        <div role="alert">
          <p>{w.unavailable}</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            {w.retry}
          </Button>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="space-y-1">
                <CardTitle>
                  {w.count(filtered.length, data.courses.length)}
                </CardTitle>
                <p className="text-meta text-muted-foreground">
                  {w.readAt(
                    formatTimestamp(
                      new Date(query.dataUpdatedAt).toISOString(),
                    ),
                    query.isError,
                  )}
                </p>
              </div>
              <Button variant="outline" onClick={() => void query.refetch()}>
                {w.refresh}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {query.isError && <p role="status">{w.stale}</p>}
            {data.availability !== "enabled" && (
              <p role="status" className="text-ui text-muted-foreground">
                {data.availability === "disabled"
                  ? w.disabled
                  : w.configuration}
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <div className="space-y-2">
                <Label htmlFor="drive-search">{w.search}</Label>
                <Input
                  id="drive-search"
                  type="search"
                  placeholder={w.searchHint}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="drive-term">{w.term}</Label>
                <NativeSelect
                  id="drive-term"
                  value={term}
                  onChange={(event) => setTerm(event.target.value)}
                >
                  <option value="current">{w.current}</option>
                  <option value="past">{w.past}</option>
                  <option value="all">{w.all}</option>
                </NativeSelect>
              </div>
            </div>
            <ul>
              {data.courses.map((course) => (
                <CourseFolder
                  key={course.course_id}
                  course={course}
                  visible={filtered.some(
                    (row) => row.course_id === course.course_id,
                  )}
                  query={query}
                />
              ))}
            </ul>
            {!filtered.length && (
              <p className="text-ui text-muted-foreground">
                {data.courses.length ? w.empty : w.none}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
